import {
  BillingDetailsResponse,
  SubscriptionBillingInterval,
  SubscriptionPlanType,
} from "@coeff/api";

import { PricingSummary as PricingSummaryResponseType } from "../types";

// Pure pricing math for the billing surfaces (ENG-8391). Kept free of runtime
// dependencies so it stays unit-testable under jest.

export const getPricingSummary = (
  planType: SubscriptionPlanType | undefined,
  billingInterval: SubscriptionBillingInterval,
  monthlyPrice: number,
  annualMonthlyPrice: number,
  additionalDataSouceCost: number,
  usersCount: number,
  dataSourcesCount: number,
  webDashboardMonthlyPrice: number,
  webDashboardAnnualMonthlyPrice: number,
  includeWebDashboards: boolean,
  discountPercentage?: number
): PricingSummaryResponseType => {
  const oneUserCost = (billingInterval === "annually" ? annualMonthlyPrice : monthlyPrice) * 1;

  const nonDiscountedOneUserCost = monthlyPrice;
  const usersCost =
    (billingInterval === "annually" ? annualMonthlyPrice : monthlyPrice) * (usersCount || 1);

  const oneDataSourceCost = (() => {
    if (dataSourcesCount <= 1) {
      return 0;
    }
    return additionalDataSouceCost * (usersCount || 1);
  })();

  const dataSourceCost = oneDataSourceCost * (dataSourcesCount - 1);

  // The AI Dashboards addon is a flat per-subscription price (not per-user), so it's excluded
  // from the per-user annual-discount math below. Like the user plan, it's displayed at its
  // monthly price with the annual saving broken out separately.
  const webDashboardCost = includeWebDashboards
    ? billingInterval === "annually"
      ? webDashboardAnnualMonthlyPrice
      : webDashboardMonthlyPrice
    : 0;
  const webDashboardAnnualSavings =
    includeWebDashboards && billingInterval === "annually"
      ? webDashboardMonthlyPrice - webDashboardAnnualMonthlyPrice
      : 0;

  let totalMonthlyCost = usersCost + dataSourceCost;

  let discountPrice = 0;
  if (discountPercentage) {
    discountPrice = (totalMonthlyCost / 100) * discountPercentage;
  }
  totalMonthlyCost = totalMonthlyCost - discountPrice + webDashboardCost;

  const dueNowCost = billingInterval === "annually" ? totalMonthlyCost * 12 : totalMonthlyCost;

  // An annual per-seat price of 0 means the seats are included (the value is carried
  // by an addon), not discounted from the monthly list price -- no savings to report.
  const annualDiscount =
    billingInterval === "monthly" || annualMonthlyPrice === 0
      ? 0
      : (usersCount || 1) * (monthlyPrice - annualMonthlyPrice);

  return {
    usersCost,
    dataSourceCost,
    webDashboardCost,
    webDashboardAnnualSavings,
    totalMonthlyCost,
    dueNowCost,
    annualDiscount,
    oneUserCost,
    nonDiscountedOneUserCost,
    oneDataSourceCost,
    discountPrice,
  };
};

export const resolveAmountDueNow = (
  editType: string,
  frontEndEstimate: number,
  billingDetails?: BillingDetailsResponse
): number => {
  if (editType === "remove-seats") {
    // A seat reduction takes effect at term end and charges nothing today. The
    // backend estimate for it is the next-term renewal invoice, so its amount_due is
    // a next-term total, never a charge due now. This mirrors the backend's
    // modify_immediately in get_billing_details and is_effective_immediately in
    // _update_subscription (subscription_controller.py) -- keep the three in
    // lockstep.
    return 0;
  }
  if (!billingDetails) {
    return frontEndEstimate;
  }
  if (billingDetails.is_refund) {
    return billingDetails.existing_credits || frontEndEstimate;
  }
  return billingDetails.amount_due === undefined ? frontEndEstimate : billingDetails.amount_due;
};

export type PlanCardSeatPrice = { type: "included" } | { type: "price"; value: number };

export const getPlanCardSeatPrice = (
  perItemMonthlyPriceUsd: number | undefined,
  seatPriceCarriedByAddon: boolean | undefined,
  listPrice: number
): PlanCardSeatPrice => {
  if (perItemMonthlyPriceUsd === 0) {
    // A genuine $0 seat price (contract value carried by the AI Dashboards addon)
    // reads "Included". A 0 that nothing carries is stale data, so fall back to the
    // list price -- a card must never read "$0 user / month".
    return seatPriceCarriedByAddon ? { type: "included" } : { type: "price", value: listPrice };
  }
  return { type: "price", value: perItemMonthlyPriceUsd ?? listPrice };
};
